import type {
  QueryProcessorService,
  QueryProcessorServiceNormalizeParams,
} from '@empathyco/playboard-core-domain'
import type { Ref } from 'vue'
import { ref } from 'vue'
import { CONTAINER } from '../config/inversify.config'
import { TYPES } from '../config/inversify.types'

export interface NormalizeQueryComposable {
  normalizedQuery: Ref<string | undefined>
  userQuery: Ref<string | undefined>
  setNormalizedQuery: (params: Partial<QueryProcessorServiceNormalizeParams>) => Promise<void>
  normalizeQuery: (params: QueryProcessorServiceNormalizeParams) => Promise<string | undefined>
}

export function useNormalizeQuery(): NormalizeQueryComposable {
  const queryProcessorService = CONTAINER.get<QueryProcessorService>(TYPES.QueryProcessorService)

  const normalizedQueries: { [lang: string]: { [query: string]: string | undefined } } = {}

  const normalizedQuery = ref<string | undefined>(undefined)

  const userQuery = ref<string | undefined>(undefined)

  async function setNormalizedQuery(
    params: Partial<QueryProcessorServiceNormalizeParams>,
  ): Promise<void> {
    userQuery.value = params.query
    normalizedQuery.value = undefined

    if (params.query && params.lang) {
      normalizedQuery.value = await normalizeQuery(params as QueryProcessorServiceNormalizeParams)
    }
  }

  async function normalizeQuery(
    params: QueryProcessorServiceNormalizeParams,
  ): Promise<string | undefined> {
    if (normalizedQueries[params.lang]?.hasOwnProperty(params.query)) {
      return normalizedQueries[params.lang][params.query]
    }
    let normalizedQuery: string | undefined
    const { qNormalized } = await queryProcessorService.normalizeQuery(params)
    if (qNormalized !== params.query) {
      normalizedQuery = qNormalized
    }
    if (!normalizedQueries.hasOwnProperty(params.lang)) {
      normalizedQueries[params.lang] = {}
    }
    normalizedQueries[params.lang][params.query] = normalizedQuery
    return normalizedQuery
  }

  return {
    normalizedQuery,
    userQuery,
    setNormalizedQuery,
    normalizeQuery,
  }
}
